import { motion } from 'framer-motion';
import { Brush, Copy, FileCheck, Mail, UserX } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import type { DataHygieneResult } from '../types/org.types';

function severityFromScore(score: number): 'critical' | 'medium' | 'low' {
  if (score < 50) return 'critical';
  if (score < 75) return 'medium';
  return 'low';
}

function severityColor(score: number): string {
  if (score < 50) return '#D64545';
  if (score < 75) return '#C7851A';
  return '#3FA96B';
}

function SignalCard({
  title,
  icon,
  headline,
  details,
  score,
}: {
  title: string;
  icon: React.ReactNode;
  headline: string;
  details: { label: string; value: string }[];
  score: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="bg-surface-1 border border-border rounded-lg p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500">
          {icon}
          {title}
        </div>
        <span
          className="text-xs tabular-nums font-semibold"
          style={{ color: severityColor(score) }}
        >
          {score}/100
        </span>
      </div>
      <div className="text-lg text-crux-light font-semibold mb-3">{headline}</div>
      <div className="space-y-1.5">
        {details.map((d) => (
          <div key={d.label} className="flex justify-between text-sm">
            <span className="text-neutral-500">{d.label}</span>
            <span className="text-crux-light tabular-nums">{d.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function DataHygienePage({ data }: { data?: DataHygieneResult | null } = {}) {
  const storeDh = useOrgStore((s) => s.audit.datahygiene);
  const scanMode = data !== undefined;
  const dh = scanMode ? data : storeDh;

  if (!dh) {
    return (
      <EmptyState
        title="Data hygiene audit not loaded"
        hint="Reconnect to scan again."
        icon={<Brush size={20} />}
      />
    );
  }

  const summary = (() => {
    const parts: string[] = [];
    if (dh.duplicates.objectsCovered.length < 3) {
      parts.push(`duplicate rules cover ${dh.duplicates.objectsCovered.length} of 3 core objects`);
    }
    if (dh.staleness.orphanOwnedPercent > 5) {
      parts.push(`${dh.staleness.orphanOwnedPercent}% of records owned by inactive users`);
    }
    if (dh.completeness.avgPercent < 80) {
      parts.push(`${dh.completeness.avgPercent}% average field completeness`);
    }
    if (dh.validity.badFormatPercent > 5 || dh.validity.bouncedPercent > 5) {
      parts.push(`${dh.validity.badFormatPercent}% malformed · ${dh.validity.bouncedPercent}% bounced emails`);
    }
    if (parts.length === 0) return 'Data quality looks healthy across all four signal categories.';
    return parts.join(' · ');
  })();

  const completenessPerObject = Object.entries(dh.completeness.perObject)
    .map(([k, v]) => `${k} ${v}%`)
    .join(' · ');

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-grow mb-1">Audit</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Data Hygiene</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Duplicate management, field completeness, email validity, record staleness, orphan ownership.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="bg-surface-1 border border-border rounded-lg p-6 flex items-center gap-6"
      >
        <div className="flex flex-col items-center">
          <div
            className="text-4xl font-semibold tabular-nums"
            style={{ color: severityColor(dh.subScore) }}
          >
            {dh.subScore}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">/100</div>
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">
            Data Hygiene sub-score
          </div>
          <div className="text-sm text-crux-light">{summary}</div>
        </div>
        <RiskBadge tone={severityFromScore(dh.subScore)}>
          {dh.subScore >= 75 ? 'Healthy' : dh.subScore >= 50 ? 'Watch' : 'Action'}
        </RiskBadge>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <SignalCard
          title="Duplicates"
          icon={<Copy size={14} />}
          headline={`${dh.duplicates.objectsCovered.length} of 3 core objects covered`}
          score={dh.duplicates.score}
          details={[
            { label: 'Active duplicate rules', value: dh.duplicates.rulesActive.toString() },
            { label: 'Active matching rules', value: dh.duplicates.matchingRulesActive.toString() },
            {
              label: 'Unresolved sets (90d)',
              value: dh.duplicates.unresolvedSets90d.toLocaleString(),
            },
          ]}
        />

        <SignalCard
          title="Completeness"
          icon={<FileCheck size={14} />}
          headline={`${dh.completeness.avgPercent}% average field completeness`}
          score={dh.completeness.score}
          details={[{ label: 'Per-object', value: completenessPerObject || '-' }]}
        />

        <SignalCard
          title="Validity"
          icon={<Mail size={14} />}
          headline={`${dh.validity.badFormatPercent}% malformed · ${dh.validity.bouncedPercent}% bounced`}
          score={dh.validity.score}
          details={[
            {
              label: 'Bad-format Contacts',
              value: dh.validity.badFormatEmailContacts.toLocaleString(),
            },
            { label: 'Bad-format Leads', value: dh.validity.badFormatEmailLeads.toLocaleString() },
            { label: 'Bounced Contacts', value: dh.validity.bouncedContacts.toLocaleString() },
            { label: 'Bounced Leads', value: dh.validity.bouncedLeads.toLocaleString() },
          ]}
        />

        <SignalCard
          title="Staleness & ownership"
          icon={<UserX size={14} />}
          headline={`${dh.staleness.orphanOwnedPercent}% records owned by inactive users`}
          score={dh.staleness.score}
          details={[
            { label: 'Stale (>12mo)', value: `${dh.staleness.stalePercent}%` },
            { label: 'Top orphan object', value: dh.staleness.topOrphanObject ?? '-' },
          ]}
        />
      </div>

      {Object.keys(dh.staleness.perObject).length > 0 && (
        <CollapsibleSection title="Per-object staleness & ownership">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-left py-2">Object</th>
                <th className="text-right py-2">Records</th>
                <th className="text-right py-2">Stale</th>
                <th className="text-right py-2">Orphan-owned</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(dh.staleness.perObject).map(([obj, stats]) => (
                <tr key={obj} className="border-t border-border/60">
                  <td className="py-2 text-crux-light">{obj}</td>
                  <td className="py-2 text-right tabular-nums">{stats.total.toLocaleString()}</td>
                  <td className="py-2 text-right tabular-nums">{stats.stale.toLocaleString()}</td>
                  <td className="py-2 text-right tabular-nums">{stats.orphan.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CollapsibleSection>
      )}

      {dh.customObjects.length > 0 && (
        <CollapsibleSection title="Top custom objects: orphan ownership (informational)">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-left py-2">Object</th>
                <th className="text-right py-2">Records</th>
                <th className="text-right py-2">Inactive-owned</th>
                <th className="text-right py-2">%</th>
              </tr>
            </thead>
            <tbody>
              {dh.customObjects.map((co) => (
                <tr key={co.apiName} className="border-t border-border/60">
                  <td className="py-2 text-crux-light">{co.label}</td>
                  <td className="py-2 text-right tabular-nums">{co.records.toLocaleString()}</td>
                  <td className="py-2 text-right tabular-nums">
                    {co.inactiveOwned.toLocaleString()}
                  </td>
                  <td className="py-2 text-right tabular-nums">{co.percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 text-[11px] text-neutral-600">
            Excluded from sub-score. Custom objects vary too much across orgs to score fairly.
          </div>
        </CollapsibleSection>
      )}

      {dh.notes.length > 0 && (
        <div className="text-[11px] text-neutral-600 space-y-1">
          {dh.notes.map((n, i) => (
            <p key={i}>· {n}</p>
          ))}
        </div>
      )}
    </div>
  );
}
